/**
 * File Upload Zone Component
 * Drag-and-drop file upload area
 */

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper, Button, IconButton } from '@mui/material';
import { CloudUpload as UploadIcon, Close as CloseIcon, Description as FileIcon } from '@mui/icons-material';

interface FileUploadZoneProps {
  title: string;
  accept: { [key: string]: string[] }; // MIME type mapping, e.g., { 'application/pdf': ['.pdf'] }
  onFileSelect: (file: File) => void;
  uploadedFile: { filename: string; url: string } | null;
  onClear: () => void;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  title,
  accept,
  onFileSelect,
  uploadedFile,
  onClear,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept,
    multiple: false,
  });

  if (uploadedFile) {
    return (
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileIcon sx={{ color: '#6c757d' }} />
            <Box>
              <Typography variant="body1" fontWeight={600} sx={{ color: '#000' }}>
                {uploadedFile.filename.split('_').slice(1).join('_')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6c757d' }}>
                {title}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClear} color="error" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      {...getRootProps()}
      sx={{
        p: 4,
        mb: 2,
        textAlign: 'center',
        cursor: 'pointer',
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
        },
      }}
    >
      <input {...getInputProps()} />
      <UploadIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
      <Typography variant="h6" gutterBottom sx={{ color: '#212529' }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6c757d' }}>
        {isDragActive
          ? 'Drop file here...'
          : 'Drag & drop file here, or click to browse'}
      </Typography>
    </Paper>
  );
};

export default FileUploadZone;

